import React from 'react';
import { UserX, AlertCircle, LogOut, Clock, Calendar, History, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const WT_LABEL = { verbal: 'Verbal', written: 'Written', written2: 'Written #2', final: 'Final' };
const WT_COLOR = {
  verbal: 'bg-yellow-100 text-yellow-700', written: 'bg-orange-100 text-orange-700',
  written2: 'bg-orange-100 text-orange-800', final: 'bg-red-100 text-red-700',
};

const SuspendedEmployeesCard = ({ currentlySuspended, upcomingSuspensions, pastSuspensions }) => {
  const totalActive = (currentlySuspended?.length || 0);
  const totalUpcoming = (upcomingSuspensions?.length || 0);
  const totalPast = (pastSuspensions?.length || 0);
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2">
          <UserX className="h-5 w-5 text-orange-600" />
          Suspension Records
        </CardTitle>
        <div className="flex gap-1">
          {totalActive > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700">
              {totalActive} Active
            </Badge>
          )}
          {totalUpcoming > 0 && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
              {totalUpcoming} Upcoming
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Currently Suspended */}
        {currentlySuspended && currentlySuspended.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-red-700 flex items-center gap-1 mb-2">
              <Clock className="h-4 w-4" /> Currently Suspended
            </h4>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {currentlySuspended.map((susp) => (
                <div key={susp.id} className="p-2 bg-red-50 rounded border border-red-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{susp.name}</p>
                      <p className="text-xs text-gray-600">{susp.employee_code} - {susp.department}</p>
                    </div>
                    <Badge variant="outline" className="bg-red-100 text-red-700">
                      {susp.days_remaining} days left
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(susp.from_date), 'MMM dd')} - {format(new Date(susp.to_date), 'MMM dd, yyyy')}
                    {susp.reason && susp.reason !== 'N/A' && ` | ${susp.reason}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Suspensions */}
        {upcomingSuspensions && upcomingSuspensions.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-yellow-700 flex items-center gap-1 mb-2">
              <Calendar className="h-4 w-4" /> Upcoming Suspensions
            </h4>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {upcomingSuspensions.map((susp) => (
                <div key={susp.id} className="p-2 bg-yellow-50 rounded border border-yellow-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{susp.name}</p>
                      <p className="text-xs text-gray-600">{susp.employee_code} - {susp.department}</p>
                    </div>
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
                      Starts in {susp.days_until_start} days
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(susp.from_date), 'MMM dd')} - {format(new Date(susp.to_date), 'MMM dd, yyyy')} ({susp.days} days)
                    {susp.reason && susp.reason !== 'N/A' && ` | ${susp.reason}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Suspensions (History) */}
        {pastSuspensions && pastSuspensions.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-600 flex items-center gap-1 mb-2">
              <History className="h-4 w-4" /> Recent Suspension History
            </h4>
            <div className="space-y-2 max-h-36 overflow-y-auto">
              {pastSuspensions.slice(0, 5).map((susp) => (
                <div key={susp.id} className="p-2 bg-gray-50 rounded border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{susp.name}</p>
                      <p className="text-xs text-gray-600">{susp.employee_code} - {susp.department}</p>
                    </div>
                    <Badge variant="outline" className="bg-gray-100 text-gray-600">
                      {susp.days} days
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(susp.from_date), 'MMM dd')} - {format(new Date(susp.to_date), 'MMM dd, yyyy')}
                    {susp.reason && susp.reason !== 'N/A' && ` | ${susp.reason}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No suspensions message */}
        {(!currentlySuspended || currentlySuspended.length === 0) && 
         (!upcomingSuspensions || upcomingSuspensions.length === 0) && 
         (!pastSuspensions || pastSuspensions.length === 0) && (
          <p className="text-gray-500 text-center py-4">No suspension records</p>
        )}
      </CardContent>
    </Card>
  );
};

const WarningLettersCard = ({ activeWarnings }) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          Active Warning Letters
        </CardTitle>
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
          {activeWarnings?.length || 0}
        </Badge>
      </CardHeader>
      <CardContent>
        {!activeWarnings || activeWarnings.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No active warning letters</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {activeWarnings.map((warning, idx) => {
              const wt = warning.warning_type || 'written';
              return (
              <div key={idx} className="p-2 bg-yellow-50 rounded border border-yellow-200" data-testid={`dash-warning-${warning.employee_code}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{warning.name}</p>
                    <p className="text-xs text-gray-600">{warning.employee_code} - {warning.department}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className={`text-[10px] ${WT_COLOR[wt] || 'bg-gray-100 text-gray-700'}`}>
                      {WT_LABEL[wt] || wt}
                    </Badge>
                    <Badge variant="outline" className={warning.days_remaining <= 30 ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}>
                      {warning.days_remaining}d left
                    </Badge>
                    {warning.warning_id && (
                      <button
                        type="button"
                        onClick={() => window.open(`${API_BASE}/api/warning-letters/${warning.employee_id}/${warning.warning_id}/pdf`, '_blank')}
                        className="text-rose-600 hover:text-rose-700"
                        title="Download warning letter PDF"
                        data-testid={`dash-warning-pdf-${warning.employee_code}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Issued: {format(new Date(warning.issue_date), 'MMM dd, yyyy')} - {warning.description || warning.reason}</p>
              </div>
            );})}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ResignedEmployeesCard = ({ resigned }) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2">
          <LogOut className="h-5 w-5 text-red-600" />
          Resigned Employees
        </CardTitle>
        <Badge variant="outline" className="bg-red-50 text-red-700">
          {resigned?.length || 0}
        </Badge>
      </CardHeader>
      <CardContent>
        {!resigned || resigned.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No resigned employees</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {resigned.map((emp) => (
              <div key={emp.id} className="p-2 bg-red-50 rounded border border-red-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{emp.name}</p>
                    <p className="text-xs text-gray-600">{emp.employee_code} - {emp.department}</p>
                  </div>
                </div>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  <span>Resigned: {emp.resignation_date !== 'N/A' ? format(new Date(emp.resignation_date), 'MMM dd, yyyy') : 'N/A'}</span>
                  <span>Last Day: {emp.last_working_date !== 'N/A' ? format(new Date(emp.last_working_date), 'MMM dd, yyyy') : 'N/A'}</span>
                </div>
                {emp.reason !== 'N/A' && <p className="text-xs text-gray-500 mt-1">Reason: {emp.reason}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TerminatedEmployeesCard = ({ terminated }) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2">
          <UserX className="h-5 w-5 text-red-600" />
          Terminated Employees
        </CardTitle>
        <Badge variant="outline" className="bg-red-50 text-red-700">
          {terminated?.length || 0}
        </Badge>
      </CardHeader>
      <CardContent>
        {!terminated || terminated.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No terminated employees</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {terminated.map((emp) => (
              <div key={emp.id} className="p-2 bg-red-50 rounded border border-red-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{emp.name}</p>
                    <p className="text-xs text-gray-600">{emp.employee_code} - {emp.department}</p>
                  </div>
                </div>
                <div className="flex gap-4 mt-1 text-xs text-gray-500">
                  <span>Terminated: {emp.termination_date !== 'N/A' ? format(new Date(emp.termination_date), 'MMM dd, yyyy') : 'N/A'}</span>
                  <span>Last Day: {emp.last_working_date !== 'N/A' ? format(new Date(emp.last_working_date), 'MMM dd, yyyy') : 'N/A'}</span>
                </div>
                {emp.reason !== 'N/A' && <p className="text-xs text-gray-500 mt-1">Reason: {emp.reason}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { SuspendedEmployeesCard, WarningLettersCard, ResignedEmployeesCard, TerminatedEmployeesCard };
